/**
 * Microsoft Graph API Utilities
 *
 * This module provides utilities for calling Microsoft Graph API.
 *
 * Token acquisition is handled by MSAL.js (see msalProvider.ts).
 * The token is cached in localStorage as 'msAccessToken'.
 *
 * Usage:
 * 1. Use the useMicrosoftGraph hook for React components
 * 2. Or use window.msal.loginAndGetToken() from console for testing
 */

import { localStorageProvider } from "../AppController";
import {
  acquireTokenSilent,
  getAllAccounts,
  isMsalInitialized,
  DEFAULT_CALENDAR_SCOPES,
} from "../hexagon/adapters/driven/providers/msalProvider";

// Microsoft Graph API base URL
const GRAPH_API_BASE = "https://graph.microsoft.com/v1.0";

/**
 * Check if we have a Microsoft Graph access token stored
 */
export const hasMicrosoftGraphToken = (): boolean => {
  const msToken = localStorageProvider.get("msAccessToken");
  return !!msToken && msToken.length > 0;
};

/**
 * Get the Microsoft Graph access token
 * Returns null if not available
 */
export const getMicrosoftGraphToken = (): string | null => {
  return localStorageProvider.get("msAccessToken");
};

/**
 * Store the Microsoft Graph access token
 */
export const setMicrosoftGraphToken = (token: string): void => {
  localStorageProvider.set("msAccessToken", token);
};

/**
 * Clear the Microsoft Graph access token
 */
export const clearMicrosoftGraphToken = (): void => {
  localStorageProvider.remove("msAccessToken");
};

/**
 * Get a fresh token, preferring MSAL silent acquisition over cached localStorage token
 */
const getFreshToken = async (): Promise<string | null> => {
  // Try to get a fresh token from MSAL first
  if (isMsalInitialized()) {
    const accounts = getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await acquireTokenSilent(
          DEFAULT_CALENDAR_SCOPES,
          accounts[0]
        );
        if (result?.accessToken) {
          // Update localStorage with the fresh token
          localStorageProvider.set("msAccessToken", result.accessToken);
          return result.accessToken;
        }
      } catch (error) {
        // Silent acquisition failed - do not use stale localStorage token
        // (e.g. after Cognito/SSO 60-min logout or token expiry)
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (
          errorMessage.includes("AADSTS50194") ||
          errorMessage.includes("multi-tenant") ||
          errorMessage.includes("invalid_request")
        ) {
          console.warn(
            "[Microsoft Graph] Token refresh failed due to tenant mismatch. User needs to re-authenticate."
          );
        } else {
          console.warn(
            "[Microsoft Graph] Silent token acquisition failed (e.g. session expired). Re-authenticate via Connect Calendar."
          );
        }
        clearMicrosoftGraphToken();
        return null;
      }
    }
  }

  // Only use localStorage token if we never had accounts (e.g. redirect just completed)
  return getMicrosoftGraphToken();
};

/**
 * Generic Microsoft Graph API call
 */
export const callGraphApi = async <T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<{ success: boolean; data?: T; error?: string }> => {
  // Always try to get a fresh token to avoid stale token issues
  const token = await getFreshToken();

  if (!token) {
    return {
      success: false,
      error:
        "No Microsoft Graph access token available. Please connect your Microsoft account.",
    };
  }

  try {
    const response = await fetch(`${GRAPH_API_BASE}${endpoint}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Handle 401 Unauthorized - token might be expired
      if (response.status === 401 && !isRetry) {
        console.warn(
          "[Microsoft Graph] Got 401, clearing token and retrying..."
        );
        // Clear the cached token so getFreshToken will get a new one
        clearMicrosoftGraphToken();
        // Retry the request once
        return callGraphApi<T>(endpoint, options, true);
      }

      return {
        success: false,
        error:
          errorData.error?.message ||
          `Graph API error: ${response.status} ${response.statusText}`,
      };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

// ============================================
// Calendar API Types
// ============================================

export interface MicrosoftCalendar {
  id: string;
  name: string;
  color: string;
  hexColor: string;
  isDefaultCalendar: boolean;
  canEdit: boolean;
  canShare: boolean;
  canViewPrivateItems: boolean;
  owner?: {
    name: string;
    address: string;
  };
}

export interface MicrosoftCalendarEvent {
  id: string;
  subject: string;
  bodyPreview?: string; // Plain text preview of the event body/notes
  iCalUId?: string; // UID for linking to visit notes (RFC iCalendar)
  webLink?: string; // URL to open the event in Outlook web
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location?: {
    displayName: string;
  };
  organizer?: {
    emailAddress: {
      name: string;
      address: string;
    };
  };
  isAllDay: boolean;
  isCancelled: boolean;
  isOnlineMeeting: boolean;
  onlineMeetingUrl?: string;
}

export interface CalendarListResponse {
  "@odata.context": string;
  value: MicrosoftCalendar[];
}

export interface CalendarEventsResponse {
  "@odata.context": string;
  value: MicrosoftCalendarEvent[];
}

// ============================================
// Calendar API Functions
// ============================================

/**
 * Get user's calendars
 * Requires: Calendars.Read or Calendars.ReadWrite scope
 */
export const getUserCalendars = async () => {
  return callGraphApi<CalendarListResponse>("/me/calendars");
};

/**
 * Get the user's default calendar
 * Requires: Calendars.Read or Calendars.ReadWrite scope
 */
export const getDefaultCalendar = async () => {
  return callGraphApi<MicrosoftCalendar>("/me/calendar");
};

/**
 * Get calendar events
 * Requires: Calendars.Read or Calendars.ReadWrite scope
 *
 * @param calendarId - Optional calendar ID (defaults to primary calendar)
 * @param startDateTime - ISO 8601 format (e.g., "2024-01-01T00:00:00Z")
 * @param endDateTime - ISO 8601 format (e.g., "2024-01-31T23:59:59Z")
 */
export const getCalendarEvents = async (options?: {
  calendarId?: string;
  startDateTime?: string;
  endDateTime?: string;
  top?: number;
}) => {
  const { calendarId, startDateTime, endDateTime, top = 10 } = options || {};

  let endpoint = calendarId
    ? `/me/calendars/${calendarId}/events`
    : "/me/calendar/events";

  const params = new URLSearchParams();
  params.append("$top", top.toString());
  params.append("$orderby", "start/dateTime");

  // If date range specified, use calendarView instead
  if (startDateTime && endDateTime) {
    endpoint = calendarId
      ? `/me/calendars/${calendarId}/calendarView`
      : "/me/calendarView";
    params.append("startDateTime", startDateTime);
    params.append("endDateTime", endDateTime);
  }

  return callGraphApi<CalendarEventsResponse>(`${endpoint}?${params}`);
};

/**
 * Create a new calendar event
 * Requires: Calendars.ReadWrite scope
 */
export const createCalendarEvent = async (
  event: {
    subject: string;
    body?: {
      contentType: "HTML" | "Text";
      content: string;
    };
    start: {
      dateTime: string;
      timeZone: string;
    };
    end: {
      dateTime: string;
      timeZone: string;
    };
    location?: {
      displayName: string;
    };
    attendees?: Array<{
      emailAddress: {
        address: string;
        name?: string;
      };
      type: "required" | "optional" | "resource";
    }>;
  },
  calendarId?: string
) => {
  const endpoint = calendarId
    ? `/me/calendars/${calendarId}/events`
    : "/me/calendar/events";

  return callGraphApi<MicrosoftCalendarEvent>(endpoint, {
    method: "POST",
    body: JSON.stringify(event),
  });
};

/**
 * Update an existing calendar event
 * Requires: Calendars.ReadWrite scope
 *
 * @param eventId - The ID of the event to update
 * @param updates - Partial event data to update (only include fields you want to change)
 * @param calendarId - Optional calendar ID (defaults to primary calendar)
 *
 * @see https://learn.microsoft.com/en-us/graph/api/event-update
 */
export const updateCalendarEvent = async (
  eventId: string,
  updates: {
    subject?: string;
    body?: {
      contentType: "HTML" | "Text";
      content: string;
    };
    start?: {
      dateTime: string;
      timeZone: string;
    };
    end?: {
      dateTime: string;
      timeZone: string;
    };
    location?: {
      displayName: string;
    };
    attendees?: Array<{
      emailAddress: {
        address: string;
        name?: string;
      };
      type: "required" | "optional" | "resource";
    }>;
  },
  calendarId?: string
) => {
  const endpoint = calendarId
    ? `/me/calendars/${calendarId}/events/${eventId}`
    : `/me/calendar/events/${eventId}`;

  return callGraphApi<MicrosoftCalendarEvent>(endpoint, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
};

// ============================================
// User Profile API
// ============================================

export interface MicrosoftUserProfile {
  id: string;
  displayName: string;
  givenName: string;
  surname: string;
  mail: string;
  userPrincipalName: string;
  jobTitle?: string;
  officeLocation?: string;
  mobilePhone?: string;
  businessPhones?: string[];
}

/**
 * Get the current user's profile
 * Requires: User.Read scope
 */
export const getUserProfile = async () => {
  return callGraphApi<MicrosoftUserProfile>("/me");
};

// ============================================
// Debug/Test Utilities
// ============================================

/**
 * Test Microsoft Graph API connectivity
 * This will attempt to call the /me endpoint to verify token validity
 */
export const testGraphApiConnection = async () => {
  const token = getMicrosoftGraphToken();
  if (!token) {
    console.error(
      "[Microsoft Graph] No access token found. Token needs to be stored during Microsoft SSO login."
    );
    console.warn("[Microsoft Graph] Current tokens in localStorage:", {
      sso: localStorageProvider.get("sso"),
      microsoftSso: localStorageProvider.get("microsoftSso"),
      accessToken: localStorageProvider.get("accessToken")
        ? "present"
        : "not set",
      msAccessToken: localStorageProvider.get("msAccessToken")
        ? "present"
        : "not set",
    });
    return {
      success: false,
      error:
        "No Microsoft Graph access token. Backend needs to return Microsoft access token during SSO login.",
    };
  }

  // Try to get user profile as a connectivity test
  const result = await getUserProfile();

  if (result.success) {
    console.warn(
      "[Microsoft Graph] Connection successful! User:",
      result.data?.displayName
    );
  } else {
    console.error("[Microsoft Graph] Connection failed:", result.error);
  }

  return result;
};

/**
 * Debug helper to log current auth state related to Microsoft
 */
export const debugMicrosoftAuthState = () => {
  const state = {
    sso: localStorageProvider.get("sso"),
    microsoftSso: localStorageProvider.get("microsoftSso"),
    accessToken: localStorageProvider.get("accessToken")
      ? `${localStorageProvider.get("accessToken")?.substring(0, 50)}...`
      : "not set",
    idToken: localStorageProvider.get("idToken")
      ? `${localStorageProvider.get("idToken")?.substring(0, 50)}...`
      : "not set",
    msAccessToken: localStorageProvider.get("msAccessToken")
      ? `${localStorageProvider.get("msAccessToken")?.substring(0, 50)}...`
      : "not set",
  };
  if (typeof window !== "undefined" && (window as any).msal) {
    const accounts = (window as any).msal.getAllAccounts?.() || [];
    (state as Record<string, unknown>).msalAccounts =
      accounts.length > 0
        ? accounts.map((a: { username: string }) => a.username)
        : "none";
  } else {
    (state as Record<string, unknown>).msal =
      "not initialized (run window.msal.initialize() first)";
  }
  console.warn("=== Microsoft Auth State Debug ===", state);
  console.warn(
    "To get a Microsoft Graph token, run: await window.msal.loginAndGetToken()"
  );
};

// Expose to window for easy testing in browser console
if (typeof window !== "undefined") {
  (window as any).microsoftGraph = {
    testConnection: testGraphApiConnection,
    debugAuthState: debugMicrosoftAuthState,
    getUserProfile,
    getUserCalendars,
    getCalendarEvents,
    getDefaultCalendar,
    createCalendarEvent,
    updateCalendarEvent,
    hasMicrosoftGraphToken,
    setMicrosoftGraphToken,
    clearMicrosoftGraphToken,
  };
}
